import businessRulesService from '@/modules/bizai/services/BusinessRulesService';

const state = {
  rules: [],
  isLoading: false,
  isSaving: false,
};

const getters = {
  getRulesForCategory: (state) => (categoryCode) => state.rules.filter((r) => r.category_code === categoryCode),
  isLoading: (state) => state.isLoading,
  isSaving: (state) => state.isSaving,
};

const mutations = {
  setRules(state, rules) {
    state.rules = rules;
  },
  setLoading(state, value) {
    state.isLoading = value;
  },
  setSaving(state, value) {
    state.isSaving = value;
  },
  updateCategoryRules(state, { categoryCode, rules }) {
    state.rules = [
      ...state.rules.filter((r) => r.category_code !== categoryCode),
      ...rules,
    ];
  },
};

const buildDiff = (originalRules, localRules) => {
  const toCreate = localRules.filter((r) => !r.uid);
  const toUpdate = localRules.filter((r) => {
    if (!r.uid) return false;
    const original = originalRules.find((o) => o.uid === r.uid);
    return original && original.content !== r.content;
  });
  const localUids = new Set(localRules.filter((r) => r.uid).map((r) => r.uid));
  const toDelete = originalRules.filter((r) => !localUids.has(r.uid));
  return { toCreate, toUpdate, toDelete };
};

const actions = {
  async fetchBusinessRules({ commit }) {
    commit('setLoading', true);
    try {
      const rules = await businessRulesService.fetchRules();
      commit('setRules', rules);
    } finally {
      commit('setLoading', false);
    }
  },

  async saveRulesForCategory({ commit, state }, { categoryCode, localRules }) {
    commit('setSaving', true);
    const originalRules = state.rules.filter((r) => r.category_code === categoryCode);
    const { toCreate, toUpdate, toDelete } = buildDiff(originalRules, localRules);

    try {
      const [createdRules, updatedRules] = await Promise.all([
        Promise.all(toCreate.map((r) => businessRulesService.createRule({ category_code: categoryCode, content: r.content }))),
        Promise.all(toUpdate.map((r) => businessRulesService.updateRule(r.uid, { content: r.content }))),
        Promise.all(toDelete.map((r) => businessRulesService.deleteRule(r.uid))),
      ]);

      const updatedMap = Object.fromEntries(updatedRules.map((r) => [r.uid, r]));
      const deletedUids = new Set(toDelete.map((r) => r.uid));

      const survivingRules = originalRules
        .filter((r) => !deletedUids.has(r.uid))
        .map((r) => updatedMap[r.uid] || r);

      commit('updateCategoryRules', {
        categoryCode,
        rules: [...survivingRules, ...createdRules],
      });
    } finally {
      commit('setSaving', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
